<template>
    <v-menu
        v-model="menu"
        :open-on-click="false"
        offset-y
        max-height="320">
        <template v-slot:activator="{ attrs }">
            <v-text-field
                v-bind="attrs"
                type="text"
                :label="label"
                :value="value"
                :loading="loading"
                :error-messages="errorMessages"
                :hint="hint"
                persistent-hint
                prepend-inner-icon="mdi-map-marker-outline"
                :append-icon="hasCoords && !keepCoords ? 'mdi-map-marker-check' : ''"
                autocomplete="off"
                clearable
                outlined
                dense
                @input="onInput"
                @click:clear="clear"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter="onEnter"
                @keydown.esc="menu = false"
            ></v-text-field>
        </template>

        <v-list dense>
            <v-list-item
                v-for="(suggestion, s) in suggestions"
                :key="suggestion.id"
                :input-value="s === highlighted"
                @click="select(suggestion)">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-map-marker</v-icon></v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title>{{ suggestion.main }}</v-list-item-title>
                    <v-list-item-subtitle v-if="suggestion.secondary">{{ suggestion.secondary }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
            <div class="text-right caption grey--text px-4 pt-1">Google Maps</div>
        </v-list>
    </v-menu>
</template>

<script>
import { importLibrary } from '@/services/GoogleMapsService'
import * as Sentry from '@sentry/vue'

const MIN_CHARS = 3

/**
 * Campo luogo con suggerimenti da Google Places che resta un testo libero:
 * scegliendo un suggerimento si valorizzano anche lat/lng (via .sync),
 * modificando a mano il testo le coordinate vengono azzerate.
 *
 * <address-autocomplete v-model="model.location" :lat.sync="model.location_lat" :lng.sync="model.location_lng" />
 *
 * Con keep-coords il testo è solo una ricerca: le coordinate cambiano solo scegliendo un suggerimento.
 */
export default {
	name: 'address-autocomplete',
	props: {
		value: String,
		lat: [Number, String],
		lng: [Number, String],
		label: {
			type: String,
			default: 'Luogo'
		},
		errorMessages: [String, Array],
		keepCoords: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			menu: false,
			loading: false,
			suggestions: [],
			highlighted: -1,
			sessionToken: null,
			unavailable: false,
			lastRequest: 0
		}
	},

	computed: {
		hasCoords() {
			const present = value => value !== null && value !== undefined && value !== ''
			return present(this.lat) && present(this.lng)
		},

		hint() {
			if (this.keepCoords) {
				return ''
			}
			if (this.hasCoords) {
				return 'Posizione trovata su Google Maps'
			}
			if (this.value && !this.unavailable) {
				return 'Testo libero: scegli un suggerimento per salvare la posizione esatta'
			}
			return ''
		}
	},

	created() {
		this.debouncedSearch = _.debounce(this.search, 300)
	},

	methods: {
		onInput(text) {
			this.$emit('input', text)

			if (this.hasCoords && !this.keepCoords) {
				this.setCoords(null, null)
			}

			this.debouncedSearch(text)
		},

		clear() {
			this.$emit('input', null)
			if (!this.keepCoords) {
				this.setCoords(null, null)
			}
			this.closeSuggestions()
		},

		setCoords(lat, lng) {
			this.$emit('update:lat', lat)
			this.$emit('update:lng', lng)
		},

		closeSuggestions() {
			this.debouncedSearch.cancel()
			this.lastRequest++
			this.loading = false
			this.suggestions = []
			this.highlighted = -1
			this.menu = false
		},

		async search(text) {
			if (this.unavailable || !text || text.trim().length < MIN_CHARS) {
				this.closeSuggestions()
				return
			}

			const request = ++this.lastRequest
			this.loading = true

			try {
				const { AutocompleteSuggestion, AutocompleteSessionToken } = await importLibrary('places')

				// una sessione raggruppa digitazione + selezione in un'unica voce di fatturazione
				if (!this.sessionToken) {
					this.sessionToken = new AutocompleteSessionToken()
				}

				const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
					input: text,
					sessionToken: this.sessionToken,
					language: 'it',
					region: 'it'
				})

				// risposta superata da una digitazione più recente
				if (request !== this.lastRequest) {
					return
				}

				this.suggestions = suggestions
					.filter(suggestion => suggestion.placePrediction)
					.map(suggestion => {
						const prediction = suggestion.placePrediction
						return {
							id: prediction.placeId,
							text: prediction.text.text,
							main: prediction.mainText ? prediction.mainText.text : prediction.text.text,
							secondary: prediction.secondaryText ? prediction.secondaryText.text : '',
							prediction
						}
					})
				this.highlighted = -1
				this.menu = this.suggestions.length > 0
			} catch (e) {
				// Places API non abilitata, key non valida o rete assente: il campo resta a testo libero
				Sentry.captureException(e)
				this.unavailable = true
				this.suggestions = []
				this.menu = false
			} finally {
				if (request === this.lastRequest) {
					this.loading = false
				}
			}
		},

		async select(suggestion) {
			this.closeSuggestions()
			this.$emit('input', suggestion.text)
			this.loading = true

			try {
				const place = suggestion.prediction.toPlace()
				await place.fetchFields({ fields: ['location'] })

				if (place.location) {
					this.setCoords(this.round(place.location.lat()), this.round(place.location.lng()))
				}
			} catch (e) {
				Sentry.captureException(e)
			} finally {
				this.loading = false
				this.sessionToken = null
			}
		},

		// 7 decimali = precisione al centimetro, entra nelle colonne decimal del DB
		round(value) {
			return Math.round(value * 1e7) / 1e7
		},

		move(step) {
			if (!this.suggestions.length) {
				return
			}
			this.menu = true
			const count = this.suggestions.length
			this.highlighted = (this.highlighted + step + count) % count
		},

		onEnter(event) {
			if (this.menu && this.highlighted >= 0) {
				event.preventDefault()
				this.select(this.suggestions[this.highlighted])
			}
		}
	},

	beforeDestroy() {
		this.debouncedSearch.cancel()
	}
}
</script>
